import { useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { configHint, isConfigured, supabase } from './lib/supabase'
import { getStats } from './lib/api'
import { Card, ErrorBox } from './components/bits'
import Login from './pages/Login'
import VerificationRequests from './pages/VerificationRequests'
import MemberRegister from './pages/MemberRegister'
import Communications from './pages/Communications'
import { AuditPage, HousesPage } from './pages/HousesAndAudit'
import PortalSettings from './pages/PortalSettings'
import Martyrs from './pages/Martyrs'
import Mfa from './pages/Mfa'

type Page =
  | 'requests'
  | 'register'
  | 'houses'
  | 'martyrs'
  | 'communications'
  | 'audit'
  | 'settings'

/** Sidebar order follows SCREENS.md §7, with the audit log added. */
const NAV: { key: Page; label: string }[] = [
  { key: 'requests', label: 'Verification requests' },
  { key: 'register', label: 'Member register' },
  { key: 'houses', label: 'Houses & courses' },
  { key: 'martyrs', label: 'Our Martyrs' },
  { key: 'communications', label: 'Communications' },
  { key: 'audit', label: 'Audit log' },
  { key: 'settings', label: 'Portal settings' },
]

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [ready, setReady] = useState(false)

  /**
   * Assurance level. `aal1` is password-only; `aal2` means a second factor has
   * been presented in this session.
   *
   * `getAuthenticatorAssuranceLevel()` returns both what the session currently
   * has and what the account *requires* — `nextLevel` becomes `aal2` as soon as
   * a factor is enrolled. Comparing the two is what tells us whether to
   * challenge. It is re-read on every auth state change, because verifying a
   * factor issues a new token and the old value goes stale immediately.
   */
  const [needsMfa, setNeedsMfa] = useState(false)
  const [mfaChecked, setMfaChecked] = useState(false)
  /** True while the user has chosen to enrol from the banner. */
  const [setupMfa, setSetupMfa] = useState(false)
  /** Enrolled already? Drives whether the banner is shown at all. */
  const [hasFactor, setHasFactor] = useState(true)

  async function refreshAal(current: Session | null): Promise<void> {
    if (!current) {
      setNeedsMfa(false)
      setMfaChecked(true)
      return
    }
    try {
      const { data, error } =
        await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (error) throw error
      // Challenge only an account that has actually enrolled a factor.
      //
      // This used to be `currentLevel !== 'aal2'`, which forced enrolment on
      // everyone who signed in — so the Secretariat met a QR code instead of
      // its own portal the moment this bundle shipped. Enrolment is offered
      // now, not imposed.
      //
      // What has not changed: a factor, once enrolled, cannot be skipped.
      // `nextLevel` flips to 'aal2' as soon as one exists, so an enrolled
      // account holding an aal1 session still lands on the challenge screen.
      //
      // The binding half was always server-side and is still not applied:
      // 0025_require_second_factor.sql makes `is_secretariat()` demand aal2 in
      // the database. Do not apply it until every Secretariat account has
      // enrolled, or all of them are locked out with no way back.
      setNeedsMfa(data.nextLevel === 'aal2' && data.currentLevel !== 'aal2')
      setHasFactor(data.nextLevel === 'aal2')
    } catch (err: unknown) {
      // An unreadable assurance level is not a reason to shut the Secretariat
      // out of its own portal. Every privileged path is enforced in the
      // database by `is_secretariat()`, not by this component — so log it and
      // let them work.
      console.error('could not read assurance level', err)
      setNeedsMfa(false)
    } finally {
      setMfaChecked(true)
    }
  }

  useEffect(() => {
    // `ready` must be set on both paths. If getSession() rejects — expired
    // refresh token, network down — an unhandled rejection leaves `ready` false
    // forever, and the portal is a blank page with nothing in the console to
    // explain it.
    supabase.auth
      .getSession()
      .then(({ data }) => {
        setSession(data.session)
        void refreshAal(data.session)
      })
      .catch((err: unknown) => {
        console.error('getSession failed', err)
      })
      .finally(() => {
        setReady(true)
      })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next)
      setMfaChecked(false)
      void refreshAal(next)
    })
    return () => sub.subscription.unsubscribe()
  }, [])

  if (!isConfigured) {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <Card>
            <h2>Configuration missing</h2>
            <p className="small" style={{ marginTop: 8 }}>
              {configHint}
            </p>
          </Card>
        </div>
      </div>
    )
  }

  if (!ready) {
    return (
      <div className="login-wrap">
        <span className="small muted">Loading…</span>
      </div>
    )
  }

  if (!session) return <Login />

  if (!mfaChecked) {
    return (
      <div className="login-wrap">
        <span className="small muted">Checking two-factor…</span>
      </div>
    )
  }

  // Two paths into this screen now. `needsMfa` is the challenge — an enrolled
  // account that has not presented its factor this session, which is not
  // skippable. `setupMfa` is the voluntary one, reached from the banner.
  if (needsMfa || setupMfa) {
    return (
      <Mfa
        onVerified={() => {
          setNeedsMfa(false)
          setSetupMfa(false)
        }}
      />
    )
  }

  return (
    <Portal
      email={session.user.email ?? ''}
      showMfaPrompt={!hasFactor}
      onSetUpMfa={() => setSetupMfa(true)}
    />
  )
}

function Portal({
  email,
  showMfaPrompt,
  onSetUpMfa,
}: {
  email: string
  showMfaPrompt: boolean
  onSetUpMfa: () => void
}) {
  const [page, setPage] = useState<Page>('requests')
  const [counts, setCounts] = useState<{ pending: number; total: number } | null>(null)
  const [accessError, setAccessError] = useState<string | null>(null)

  // One stats call on mount doubles as the access check: `admin_stats` raises
  // 42501 for a non-Secretariat caller, so a 42501 here means the signed-in
  // account should not be looking at this portal at all.
  useEffect(() => {
    getStats()
      .then((s) => setCounts({ pending: s.pendingRequests, total: s.totalOnRegister }))
      .catch((e) => setAccessError(e instanceof Error ? e.message : String(e)))
  }, [])

  async function signOut(): Promise<void> {
    await supabase.auth.signOut()
  }

  if (accessError) {
    return (
      <div className="login-wrap">
        <div className="login-card stack">
          <ErrorBox>{accessError}</ErrorBox>
          <button className="btn btn-secondary" onClick={() => void signOut()}>
            Sign out
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="shell">
      <nav className="sidebar" aria-label="Secretariat sections">
        <div className="brand">
          <div className="brand-crest">CCP</div>
          <div>
            <div className="brand-name">SECRETARIAT</div>
            <div className="brand-sub">Admin portal</div>
          </div>
        </div>

        {NAV.map((item) => (
          <button
            key={item.key}
            className={`nav-item ${page === item.key ? 'active' : ''}`}
            onClick={() => setPage(item.key)}
            aria-current={page === item.key ? 'page' : undefined}
          >
            <span>{item.label}</span>
            {item.key === 'requests' && counts && (
              <span className="nav-count">{counts.pending}</span>
            )}
            {item.key === 'register' && counts && (
              <span className="nav-count">{counts.total}</span>
            )}
            {item.key === 'houses' && <span className="nav-count">8</span>}
          </button>
        ))}

        <div className="sidebar-footer">
          <div className="muted" style={{ wordBreak: 'break-all' }}>
            {email}
          </div>
          <button
            className="btn btn-secondary btn-sm"
            style={{ marginTop: 10, width: '100%' }}
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </div>
      </nav>

      <main className="main">
        {/* Offered, not imposed. This account can approve members, read every
            record unredacted and export the whole register, so a password on
            its own is thin — but locking the Secretariat out of its own portal
            until they find an authenticator app was worse. */}
        {showMfaPrompt && (
          <div className="card" style={{ marginBottom: 'var(--s-5)' }}>
            <div className="row">
              <div>
                <strong style={{ color: 'var(--text)' }}>
                  Two-factor is off for this account
                </strong>
                <div className="small muted" style={{ marginTop: 4 }}>
                  A Secretariat account can approve members, read every record in
                  full and export the register. Takes about a minute to set up.
                </div>
              </div>
              <div className="spacer" />
              <button className="btn btn-secondary btn-sm" onClick={onSetUpMfa}>
                Set it up
              </button>
            </div>
          </div>
        )}

        {page === 'requests' && <VerificationRequests />}
        {page === 'houses' && <HousesPage />}
        {page === 'audit' && <AuditPage />}
        {page === 'register' && <MemberRegister />}
        {page === 'martyrs' && <Martyrs />}
        {page === 'communications' && <Communications />}
        {page === 'settings' && <PortalSettings />}
      </main>
    </div>
  )
}
